/**
 * What a membership costs.
 *
 * A tier stores ONE figure, the monthly rate, and the year is twelve of it.
 * Storing both would let the two published numbers disagree, and dividing an
 * annual figure back out loses a cent whenever twelve does not divide it —
 * inside the sentence somebody is agreeing to.
 *
 * Shared because the page and the server must reach the same answer from the
 * same inputs: the page says what the commit button reads and what the
 * total is, and the server quotes the same figure.
 */

export type MembershipCadence = 'monthly' | 'annual'

/** How many monthly periods a year is bought as. */
export const PERIODS_IN_A_YEAR = 12

/**
 * How a Checkout session was opened. A public join always stores a card and
 * charges nothing (`later`). A `year` session is paid in full at checkout and
 * settled by the webhook against the arrangement.
 */
export const MEMBERSHIP_SHAPES = ['year', 'later'] as const
export type MembershipShape = (typeof MEMBERSHIP_SHAPES)[number]

/** Whether a session's metadata names a shape the webhook settles. */
export function isMembershipShape(value: unknown): value is MembershipShape {
  return (MEMBERSHIP_SHAPES as readonly unknown[]).includes(value)
}

/** What one period costs at this tier, on this cadence. */
export function rateForCadence(
  monthlyCents: number,
  cadence: MembershipCadence,
): number {
  return cadence === 'annual' ? monthlyCents * PERIODS_IN_A_YEAR : monthlyCents
}

/** How far ahead a membership may be started. */
export const MAX_START_MONTHS_AHEAD = 12

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * The last day a membership may be started, from the shul's own day.
 *
 * Shared because the page offers a range and the server refuses one outside
 * it, and a page offering a date the server declines spends somebody's
 * Turnstile token to tell them so.
 */
export function furthestStart(todayISO: string): string {
  const [y, m, d] = todayISO.split('-').map(Number)
  const months = m - 1 + MAX_START_MONTHS_AHEAD
  const year = y + Math.floor(months / 12)
  const month = (months % 12) + 1
  /* Day zero of the next month is the last day of this one. A start on the 31st
     has no answer in a month holding thirty days, and the end of the month is
     what somebody picking it meant. */
  const lastOfMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return `${year}-${pad(month)}-${pad(Math.min(d, lastOfMonth))}`
}

/** A tier's yearly figure — twelve periods, derived and never stored. */
export function annualCentsFor(monthlyCents: number): number {
  return monthlyCents * PERIODS_IN_A_YEAR
}
